export type JustifiedGridItemLayout = {
    itemIndex: number;
    rowIndex: number;
    columnIndex: number;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
};

export type JustifiedGridRowLayout = {
    rowIndex: number;
    itemStartIndex: number;
    itemEndIndex: number;
    centerY: number;
    height: number;
    isComplete: boolean;
};

export type JustifiedGridLayout = {
    items: JustifiedGridItemLayout[];
    rows: JustifiedGridRowLayout[];
    contentHeight: number;
};

export type JustifiedGridLayoutOptions = {
    aspectRatios: readonly number[];
    usableWidth: number;
    gap: number;
    targetRowHeight: number;
    minimumItemsPerRow: number;
    maximumItemsPerRow: number;
    minimumItemSize?: number;
};

export function validateJustifiedGridLayoutOptions(options: JustifiedGridLayoutOptions): void {
    const {
        aspectRatios,
        usableWidth,
        gap,
        targetRowHeight,
        minimumItemsPerRow,
        maximumItemsPerRow,
        minimumItemSize = 0.1
    } = options;

    if (![usableWidth, gap, targetRowHeight, minimumItemSize].every(Number.isFinite)
        || usableWidth <= 0
        || gap < 0
        || targetRowHeight <= 0
        || minimumItemSize <= 0
        || !Number.isInteger(minimumItemsPerRow)
        || !Number.isInteger(maximumItemsPerRow)
        || minimumItemsPerRow <= 0
        || maximumItemsPerRow < minimumItemsPerRow
        || aspectRatios.some(aspectRatio => !Number.isFinite(aspectRatio) || aspectRatio <= 0)) {
        throw new RangeError('Justified grid options must contain positive dimensions, valid row counts, and positive aspect ratios.');
    }
}

export function isJustifiedGridRowComplete(
    aspectSum: number,
    itemCount: number,
    options: JustifiedGridLayoutOptions
): boolean {
    const estimatedRowWidth = aspectSum * options.targetRowHeight + options.gap * Math.max(0, itemCount - 1);
    return itemCount >= options.maximumItemsPerRow
        || (itemCount >= options.minimumItemsPerRow && estimatedRowWidth >= options.usableWidth);
}

export function calculateJustifiedGridLayout(options: JustifiedGridLayoutOptions): JustifiedGridLayout {
    const {
        aspectRatios,
        usableWidth,
        gap,
        targetRowHeight,
        minimumItemsPerRow,
        maximumItemsPerRow,
        minimumItemSize = 0.1
    } = options;

    if (aspectRatios.length === 0) return { items: [], rows: [], contentHeight: 0 };
    validateJustifiedGridLayoutOptions(options);

    const rowRanges: Array<{ startIndex: number; endIndex: number; aspectSum: number; isComplete: boolean }> = [];
    let activeRowStartIndex = 0;
    let activeRowAspectSum = 0;

    for (let index = 0; index < aspectRatios.length; index++) {
        activeRowAspectSum += aspectRatios[index];
        const rowItemCount = index - activeRowStartIndex + 1;
        if (isJustifiedGridRowComplete(activeRowAspectSum, rowItemCount, options)) {
            rowRanges.push({ startIndex: activeRowStartIndex, endIndex: index, aspectSum: activeRowAspectSum, isComplete: true });
            activeRowStartIndex = index + 1;
            activeRowAspectSum = 0;
        }
    }

    if (activeRowStartIndex < aspectRatios.length) {
        rowRanges.push({
            startIndex: activeRowStartIndex,
            endIndex: aspectRatios.length - 1,
            aspectSum: activeRowAspectSum,
            isComplete: false
        });
    }

    const items: JustifiedGridItemLayout[] = [];
    const rows: JustifiedGridRowLayout[] = [];
    const leftEdgeX = -usableWidth * 0.5;
    let rowTopY = 0;

    for (let rowIndex = 0; rowIndex < rowRanges.length; rowIndex++) {
        const row = rowRanges[rowIndex];
        const itemCount = row.endIndex - row.startIndex + 1;
        const totalGapWidth = gap * Math.max(0, itemCount - 1);
        const rowHeight = row.isComplete && row.aspectSum > 0
            ? Math.max(minimumItemSize, (usableWidth - totalGapWidth) / row.aspectSum)
            : targetRowHeight;
        const centerY = rowTopY - rowHeight * 0.5;
        let cursorX = leftEdgeX;

        for (let itemIndex = row.startIndex; itemIndex <= row.endIndex; itemIndex++) {
            const width = Math.max(minimumItemSize, rowHeight * aspectRatios[itemIndex]);
            items.push({
                itemIndex,
                rowIndex,
                columnIndex: itemIndex - row.startIndex,
                centerX: cursorX + width * 0.5,
                centerY,
                width,
                height: rowHeight
            });
            cursorX += width + gap;
        }

        rows.push({
            rowIndex,
            itemStartIndex: row.startIndex,
            itemEndIndex: row.endIndex,
            centerY,
            height: rowHeight,
            isComplete: row.isComplete
        });
        rowTopY -= rowHeight + gap;
    }

    return { items, rows, contentHeight: Math.max(0, -rowTopY - gap) };
}
